import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import TabPanel from "@mui/lab/TabPanel";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";

import CheckboxFormField from "components/checkbox-form-field";

import type { SelectSaleConfigurationValues } from "../word-processor-select-sale-configuration-dialog.component.js";
import SettingsAccordionSummary from "./settings-accordion-summary.component.js";

interface GeneralSettingsProps {
  readonly expanded?: boolean;
  readonly onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
  readonly onExpandedStateChange?: (key: string, expanded: boolean) => void;
}

// Field mappings for each include_* field
const getDeviceFields = (
  prefix: string,
  includeColor: boolean,
  includeBattery: boolean
): string[] => {
  const fields = [
    `${prefix}_show_cpt_code`,
    `${prefix}_show_product`,
    `${prefix}_show_manufacturer`,
    `${prefix}_show_model`,
    `${prefix}_show_serial`,
    `${prefix}_show_mfr_warranty_exp`,
    `${prefix}_show_lnd_warranty_exp`,
    `${prefix}_show_lnd_deductible`,
    `${prefix}_show_price`,
    `${prefix}_show_quantity`,
    `${prefix}_show_patient_resp`,
    `${prefix}_show_insurance_resp`,
    `${prefix}_show_managed_care_resp`
  ];

  if (includeColor) {
    fields.push(`${prefix}_show_color`);
  }

  if (includeBattery) {
    fields.push(`${prefix}_show_battery`);
  }

  if (
    prefix === "hearing_aids" ||
    prefix === "domes" ||
    prefix === "receivers" ||
    prefix === "tubes" ||
    prefix === "earmolds"
  ) {
    fields.push(`${prefix}_show_ear`);
  }

  // Include device-specific fields based on prefix
  if (prefix === "earmolds") {
    fields.push(
      `${prefix}_show_material`,
      `${prefix}_show_canal_length`,
      `${prefix}_show_canal_venting`
    );
  } else if (prefix === "receivers") {
    fields.push(`${prefix}_show_gain`, `${prefix}_show_length`);
  } else if (prefix === "domes") {
    fields.push(`${prefix}_show_dome_type`, `${prefix}_show_dome_size`);
  }

  return fields;
};
export default function GeneralSettings({
  expanded,
  onChange,
  onExpandedStateChange
}: GeneralSettingsProps) {
  const { control, setValue } = useFormContext<SelectSaleConfigurationValues>();

  // Watch all include_* fields
  const includeHearingAids = useWatch({
    control,
    name: "include_hearing_aids"
  });
  const includeDomes = useWatch({ control, name: "include_domes" });
  const includeReceivers = useWatch({ control, name: "include_receivers" });
  const includeTubes = useWatch({ control, name: "include_tubes" });
  const includeEarmolds = useWatch({ control, name: "include_earmolds" });
  const includeBatteries = useWatch({ control, name: "include_batteries" });
  const includeChargers = useWatch({ control, name: "include_chargers" });
  const includeRemotes = useWatch({ control, name: "include_remotes" });
  const includeOthers = useWatch({ control, name: "include_others" });
  const includeServicesDispensingFees = useWatch({
    control,
    name: "include_services_dispensing_fees"
  });
  const includeTotals = useWatch({ control, name: "include_totals" });

  // Refs to track previous values and skip initial mount for all sync effects
  const prevIncludeHearingAids = useRef<boolean | undefined>(undefined);
  const prevIncludeDomes = useRef<boolean | undefined>(undefined);
  const prevIncludeReceivers = useRef<boolean | undefined>(undefined);
  const prevIncludeTubes = useRef<boolean | undefined>(undefined);
  const prevIncludeEarmolds = useRef<boolean | undefined>(undefined);
  const prevIncludeBatteries = useRef<boolean | undefined>(undefined);
  const prevIncludeChargers = useRef<boolean | undefined>(undefined);
  const prevIncludeRemotes = useRef<boolean | undefined>(undefined);
  const prevIncludeOthers = useRef<boolean | undefined>(undefined);
  const prevIncludeServicesDispensingFees = useRef<boolean | undefined>(
    undefined
  );
  const prevIncludeTotals = useRef<boolean | undefined>(undefined);
  const isInitialMount = useRef(true);

  // Calculate selected count for general settings
  const selectedCount = [
    includeHearingAids,
    includeDomes,
    includeReceivers,
    includeTubes,
    includeEarmolds,
    includeBatteries,
    includeChargers,
    includeRemotes,
    includeOthers,
    includeServicesDispensingFees,
    includeTotals
  ].filter(Boolean).length;

  // Sync fields when include_hearing_aids changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeHearingAids.current = includeHearingAids;
      return;
    }
    if (prevIncludeHearingAids.current !== includeHearingAids) {
      const fields = getDeviceFields("hearing_aids", true, true);
      for (const field of fields) {
        setValue(
          field as keyof SelectSaleConfigurationValues,
          includeHearingAids,
          { shouldValidate: false }
        );
      }
      // Also sync total_show_cost_of_ha when include_hearing_aids changes
      setValue("total_show_cost_of_ha", includeHearingAids ?? false, {
        shouldValidate: false
      });
      if (onExpandedStateChange) {
        onExpandedStateChange("hearing_aids", includeHearingAids ?? false);
      }
      prevIncludeHearingAids.current = includeHearingAids;
    }
  }, [includeHearingAids, setValue, onExpandedStateChange]);

  // Sync fields when include_domes changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeDomes.current = includeDomes;
      return;
    }
    if (prevIncludeDomes.current !== includeDomes) {
      const fields = getDeviceFields("domes", false, false);
      for (const field of fields) {
        setValue(field as keyof SelectSaleConfigurationValues, includeDomes, {
          shouldValidate: false
        });
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("domes", includeDomes ?? false);
      }
      prevIncludeDomes.current = includeDomes;
    }
  }, [includeDomes, setValue, onExpandedStateChange]);

  // Sync fields when include_receivers changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeReceivers.current = includeReceivers;
      return;
    }
    if (prevIncludeReceivers.current !== includeReceivers) {
      const fields = getDeviceFields("receivers", true, false);
      for (const field of fields) {
        setValue(
          field as keyof SelectSaleConfigurationValues,
          includeReceivers,
          {
            shouldValidate: false
          }
        );
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("receivers", includeReceivers ?? false);
      }
      prevIncludeReceivers.current = includeReceivers;
    }
  }, [includeReceivers, setValue, onExpandedStateChange]);

  // Sync fields when include_tubes changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeTubes.current = includeTubes;
      return;
    }
    if (prevIncludeTubes.current !== includeTubes) {
      const fields = getDeviceFields("tubes", false, false);
      for (const field of fields) {
        setValue(field as keyof SelectSaleConfigurationValues, includeTubes, {
          shouldValidate: false
        });
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("tubes", includeTubes ?? false);
      }
      prevIncludeTubes.current = includeTubes;
    }
  }, [includeTubes, setValue, onExpandedStateChange]);

  // Sync fields when include_earmolds changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeEarmolds.current = includeEarmolds;
      return;
    }
    if (prevIncludeEarmolds.current !== includeEarmolds) {
      const fields = getDeviceFields("earmolds", false, false);
      for (const field of fields) {
        setValue(
          field as keyof SelectSaleConfigurationValues,
          includeEarmolds,
          {
            shouldValidate: false
          }
        );
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("earmolds", includeEarmolds ?? false);
      }
      prevIncludeEarmolds.current = includeEarmolds;
    }
  }, [includeEarmolds, setValue, onExpandedStateChange]);

  // Sync fields when include_batteries changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeBatteries.current = includeBatteries;
      return;
    }
    if (prevIncludeBatteries.current !== includeBatteries) {
      const fields = getDeviceFields("batteries", true, false);
      for (const field of fields) {
        setValue(
          field as keyof SelectSaleConfigurationValues,
          includeBatteries,
          {
            shouldValidate: false
          }
        );
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("batteries", includeBatteries ?? false);
      }
      prevIncludeBatteries.current = includeBatteries;
    }
  }, [includeBatteries, setValue, onExpandedStateChange]);

  // Sync fields when include_chargers changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeChargers.current = includeChargers;
      return;
    }
    if (prevIncludeChargers.current !== includeChargers) {
      const fields = getDeviceFields("chargers", false, false);
      for (const field of fields) {
        setValue(
          field as keyof SelectSaleConfigurationValues,
          includeChargers,
          {
            shouldValidate: false
          }
        );
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("chargers", includeChargers ?? false);
      }
      prevIncludeChargers.current = includeChargers;
    }
  }, [includeChargers, setValue, onExpandedStateChange]);

  // Sync fields when include_remotes changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeRemotes.current = includeRemotes;
      return;
    }
    if (prevIncludeRemotes.current !== includeRemotes) {
      const fields = getDeviceFields("remotes", false, false);
      for (const field of fields) {
        setValue(field as keyof SelectSaleConfigurationValues, includeRemotes, {
          shouldValidate: false
        });
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("remotes", includeRemotes ?? false);
      }
      prevIncludeRemotes.current = includeRemotes;
    }
  }, [includeRemotes, setValue, onExpandedStateChange]);

  // Sync fields when include_others changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeOthers.current = includeOthers;
      return;
    }
    if (prevIncludeOthers.current !== includeOthers) {
      const fields = getDeviceFields("others", false, false);
      for (const field of fields) {
        setValue(field as keyof SelectSaleConfigurationValues, includeOthers, {
          shouldValidate: false
        });
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("others", includeOthers ?? false);
      }
      prevIncludeOthers.current = includeOthers;
    }
  }, [includeOthers, setValue, onExpandedStateChange]);

  // Sync fields when include_services_dispensing_fees changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeServicesDispensingFees.current = includeServicesDispensingFees;
      return;
    }
    if (
      prevIncludeServicesDispensingFees.current !==
      includeServicesDispensingFees
    ) {
      const servicesFields = [
        "services_show_cpt_code",
        "services_show_product",
        "services_show_description",
        "services_show_price",
        "services_show_quantity"
      ];
      for (const field of servicesFields) {
        setValue(
          field as keyof SelectSaleConfigurationValues,
          includeServicesDispensingFees,
          { shouldValidate: false }
        );
      }
      if (onExpandedStateChange) {
        onExpandedStateChange(
          "services",
          includeServicesDispensingFees ?? false
        );
      }
      prevIncludeServicesDispensingFees.current = includeServicesDispensingFees;
    }
  }, [includeServicesDispensingFees, setValue, onExpandedStateChange]);

  // Sync fields when include_totals changes (only when value actually changes)
  useEffect(() => {
    if (isInitialMount.current) {
      prevIncludeTotals.current = includeTotals;
      return;
    }
    if (prevIncludeTotals.current !== includeTotals) {
      const totalsFields = [
        "total_show_services_dispensing_fees",
        "total_show_discounts",
        "total_tax",
        "total_amount",
        "total_patient_payments",
        "total_insurance_payments",
        "amount_due_from_insurance",
        "amount_due_from_patient",
        "balance_due",
        "total_show_cost_of_ha"
      ];
      for (const field of totalsFields) {
        setValue(field as keyof SelectSaleConfigurationValues, includeTotals, {
          shouldValidate: false
        });
      }
      if (onExpandedStateChange) {
        onExpandedStateChange("totals", includeTotals ?? false);
      }
      // Update the ref to track the current value
      prevIncludeTotals.current = includeTotals;
    }
  }, [includeTotals, setValue, onExpandedStateChange]);

  // Mark initial mount as complete after all effects have run
  useEffect(() => {
    if (isInitialMount.current) {
      // Use setTimeout to ensure this runs after all other effects in the same render
      setTimeout(() => {
        isInitialMount.current = false;
      }, 0);
    }
  }, []);

  const handleSelectAll = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      (setValue as any)("include_hearing_aids", true);
      (setValue as any)("include_domes", true);
      (setValue as any)("include_receivers", true);
      (setValue as any)("include_tubes", true);
      (setValue as any)("include_earmolds", true);
      (setValue as any)("include_chargers", true);
      (setValue as any)("include_batteries", true);
      (setValue as any)("include_remotes", true);
      (setValue as any)("include_others", true);
      (setValue as any)("include_services_dispensing_fees", true);
      (setValue as any)("include_totals", true);
    },
    [setValue]
  );

  const handleDeselectAll = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      (setValue as any)("include_hearing_aids", false);
      (setValue as any)("include_domes", false);
      (setValue as any)("include_receivers", false);
      (setValue as any)("include_tubes", false);
      (setValue as any)("include_earmolds", false);
      (setValue as any)("include_batteries", false);
      (setValue as any)("include_chargers", false);
      (setValue as any)("include_remotes", false);
      (setValue as any)("include_others", false);
      (setValue as any)("include_services_dispensing_fees", false);
      (setValue as any)("include_totals", false);
    },
    [setValue]
  );

  return (
    <TabPanel value="general">
      <Accordion expanded={expanded} onChange={onChange}>
        <SettingsAccordionSummary
          title="Sale Items to include in the invoice:"
          expanded={expanded}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          selectedCount={selectedCount}
        />
        <AccordionDetails>
          <Stack ml={2} mb={2} mt={-2}>
            <FormControlLabel
              label="Hearing Aids"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_hearing_aids"
                  control={control}
                />
              }
            />

            <FormControlLabel
              label="Domes"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_domes"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Receivers"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_receivers"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Tubes"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_tubes"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Earmolds"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_earmolds"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Chargers"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_chargers"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
            <FormControlLabel
              label="Batteries"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_batteries"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Remotes"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_remotes"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Others"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_others"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Services/Dispensing Fees"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="include_services_dispensing_fees"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Totals"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField name="include_totals" control={control} />
              }
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </TabPanel>
  );
}
